export const DEFAULT_RALLY_LIST_URL =
  'https://kartadrogowa.pl/api/dropdown_data.php';
export const DEFAULT_CONTROL_POINTS_TEMPLATE = `${DEFAULT_RALLY_LIST_URL}?rally_id={rallyId}`;
export const DEFAULT_TIMES_TEMPLATE =
  'https://kartadrogowa.pl/api/times_api.php?rally_id={rallyId}&point_id={pointId}';

export const DEFAULT_TIME_CONTROL_CONFIG = {
  enabled: true,
  rallyListUrl: DEFAULT_RALLY_LIST_URL,
  controlPointsUrlTemplate: DEFAULT_CONTROL_POINTS_TEMPLATE,
  timesUrlTemplate: DEFAULT_TIMES_TEMPLATE,
  defaultRallyIds: [],
  refreshIntervalMs: 10000,
};

const VALUE_KEYS = [
  'id',
  'value',
  'key',
  'code',
  'identifier',
  'rally_id',
  'rallyId',
  'point_id',
  'pointId',
];

const LABEL_KEYS = ['name', 'label', 'title', 'text', 'description', 'display'];

export const normalizeString = (value) => (value != null ? String(value).trim() : '');

export const normalizeStringLower = (value) => normalizeString(value).toLowerCase();

const escapeRegExp = (value) =>
  value == null
    ? ''
    : String(value).replace(/[.*+?^${}()|[\]\\]/g, (match) => `\\${match}`);

export const normalizeIdList = (value) => {
  const result = [];
  const seen = new Set();

  const addValue = (item) => {
    const normalized = normalizeString(item);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  };

  if (Array.isArray(value)) {
    value.forEach(addValue);
  } else if (value !== undefined && value !== null) {
    const normalizedValue = normalizeString(value);
    if (normalizedValue) {
      normalizedValue.split(',').forEach(addValue);
    }
  }

  return result;
};

export const normalizeTimeControlConfig = (config) => {
  const normalized = {
    ...DEFAULT_TIME_CONTROL_CONFIG,
    defaultRallyIds: [...DEFAULT_TIME_CONTROL_CONFIG.defaultRallyIds],
  };

  if (!config || typeof config !== 'object') {
    return normalized;
  }

  if (config.enabled !== undefined) {
    normalized.enabled = Boolean(config.enabled);
  }

  if (config.rallyListUrl !== undefined) {
    normalized.rallyListUrl = normalizeString(config.rallyListUrl);
  } else if (config.rallyDropdownUrl !== undefined) {
    normalized.rallyListUrl = normalizeString(config.rallyDropdownUrl);
  }

  if (config.controlPointsUrlTemplate !== undefined) {
    normalized.controlPointsUrlTemplate = normalizeString(
      config.controlPointsUrlTemplate
    );
  } else if (config.controlPointsUrl !== undefined) {
    normalized.controlPointsUrlTemplate = normalizeString(config.controlPointsUrl);
  }

  if (config.timesUrlTemplate !== undefined) {
    normalized.timesUrlTemplate = normalizeString(config.timesUrlTemplate);
  } else if (config.timesApiBaseUrl !== undefined) {
    const base = normalizeString(config.timesApiBaseUrl);
    if (base) {
      const separator = base.includes('?') ? (/[?&]$/.test(base) ? '' : '&') : '?';
      normalized.timesUrlTemplate = `${base}${separator}rally_id={rallyId}&point_id={pointId}`;
    } else {
      normalized.timesUrlTemplate = '';
    }
  }

  if (config.defaultRallyIds !== undefined) {
    normalized.defaultRallyIds = normalizeIdList(config.defaultRallyIds);
  } else if (config.defaultRallyId !== undefined) {
    normalized.defaultRallyIds = normalizeIdList(config.defaultRallyId);
  }

  if (config.refreshIntervalMs !== undefined) {
    const parsed = Number(config.refreshIntervalMs);
    if (Number.isFinite(parsed) && parsed > 0) {
      normalized.refreshIntervalMs = parsed;
    }
  }

  return normalized;
};

export const buildUrlWithTemplate = (
  template,
  placeholders = {},
  queryParams = {}
) => {
  const trimmedTemplate = normalizeString(template);
  if (!trimmedTemplate) {
    return '';
  }

  let url = trimmedTemplate;

  Object.entries(placeholders).forEach(([key, value]) => {
    const pattern = new RegExp(`\\u007B${escapeRegExp(key)}\\u007D`, 'gi');
    url = url.replace(pattern, encodeURIComponent(value ?? ''));
  });

  if (/\{[^}]+\}/.test(url)) {
    return '';
  }

  const query = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    const existsPattern = new RegExp(`[?&]${escapeRegExp(key)}=`, 'i');
    if (existsPattern.test(url)) {
      return;
    }
    query.append(key, String(value));
  });

  const queryString = query.toString();
  if (queryString) {
    const hasQuery = url.includes('?');
    const joiner = hasQuery ? (/[?&]$/.test(url) ? '' : '&') : '?';
    url = `${url}${joiner}${queryString}`;
  }

  return url;
};

export const getOptionLabel = (option) => {
  if (!option || typeof option !== 'object') return '';
  for (const key of LABEL_KEYS) {
    const candidate = option[key];
    if (candidate != null && candidate !== '') {
      return normalizeString(candidate);
    }
  }
  return '';
};

export const getOptionValue = (option) => {
  if (!option || typeof option !== 'object') return '';
  for (const key of VALUE_KEYS) {
    const candidate = option[key];
    if (candidate != null && candidate !== '') {
      return normalizeString(candidate);
    }
  }
  const labelFallback = getOptionLabel(option);
  return labelFallback;
};

export const ensureOptionLabel = (option, fallback) => {
  const label = getOptionLabel(option);
  if (label) return label;
  const value = getOptionValue(option);
  return value || fallback;
};
